import type { FC, ReactNode } from 'react'
import type { LegalDocument } from '@/types'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button-link'
import { Eyebrow } from '@/components/generals/eyebrow'
import { siteConfig } from '@/constants/site'
import { START_PROJECT_HREF } from '@/constants/navigation'

interface LegalPageProps {
    document: LegalDocument
}

const toAnchor = (heading: string): string => heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

export const LegalPage: FC<LegalPageProps> = ({ document }): ReactNode => {
    return (
        <article className='relative isolate overflow-hidden pb-24 pt-32 sm:pb-32 sm:pt-40'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='bg-grid absolute inset-0 opacity-50 [mask-image:radial-gradient(ellipse_70%_50%_at_50%_0%,black_10%,transparent_70%)]' />
                <div className='absolute -right-40 -top-40 size-[34rem] rounded-full bg-electric/15 blur-[140px]' />
            </div>

            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <Link
                    href='/'
                    className='group inline-flex items-center gap-2 rounded-sm text-sm text-muted-foreground outline-none transition-colors duration-300 hover:text-white focus-visible:ring-2 focus-visible:ring-ring'
                >
                    <ArrowLeft aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-x-1' />
                    Back to home
                </Link>

                <header className='mt-10 max-w-3xl'>
                    <Eyebrow>Legal</Eyebrow>

                    <h1 className='font-display mt-5 text-[clamp(2.4rem,7vw,4.5rem)] font-extrabold uppercase leading-[0.92] tracking-[-0.025em] text-white'>
                        {document.title}
                    </h1>

                    <p className='mt-6 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground'>
                        Last updated {document.updated}
                    </p>

                    <p className='mt-8 text-lg leading-relaxed text-muted-foreground'>
                        {document.intro}
                    </p>
                </header>

                <div className='mt-16 grid gap-12 lg:grid-cols-12 lg:gap-16'>
                    <nav aria-label='On this page' className='hidden lg:col-span-4 lg:block'>
                        <div className='sticky top-28 rounded-2xl border border-white/10 bg-white/[0.02] p-6'>
                            <p className='font-mono text-[0.68rem] uppercase tracking-[0.18em] text-electric-soft'>
                                On this page
                            </p>

                            <ol className='mt-5 space-y-3'>
                                {document.sections.map(section => (
                                    <li key={section.heading}>
                                        <a
                                            href={`#${toAnchor(section.heading)}`}
                                            className='rounded-sm text-sm text-muted-foreground outline-none transition-colors duration-300 hover:text-white focus-visible:text-white focus-visible:ring-2 focus-visible:ring-ring'
                                        >
                                            {section.heading}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </nav>

                    <div className='space-y-12 lg:col-span-8'>
                        {document.sections.map((section, index) => (
                            <section key={section.heading} id={toAnchor(section.heading)} aria-labelledby={`${toAnchor(section.heading)}-title`}>
                                <h2
                                    id={`${toAnchor(section.heading)}-title`}
                                    className='flex items-baseline gap-4 text-xl font-semibold text-white sm:text-2xl'
                                >
                                    <span className='font-mono text-xs text-electric-soft'>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    {section.heading}
                                </h2>

                                <div className='mt-4 space-y-4 pl-0 leading-relaxed text-muted-foreground sm:pl-9'>
                                    {section.paragraphs.map(paragraph => (
                                        <p key={paragraph}>{paragraph}</p>
                                    ))}

                                    {section.list && (
                                        <ul className='space-y-2'>
                                            {section.list.map(item => (
                                                <li key={item} className='flex gap-3'>
                                                    <span aria-hidden='true' className='mt-2.5 size-1.5 shrink-0 rounded-full bg-electric' />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </section>
                        ))}

                        <div className='flex flex-col items-start gap-6 rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8'>
                            <p className='text-muted-foreground'>
                                Questions?{' '}
                                <a href={`mailto:${siteConfig.email}`} className='break-all text-white underline underline-offset-4 hover:text-electric-soft'>
                                    {siteConfig.email}
                                </a>
                            </p>

                            <ButtonLink href={START_PROJECT_HREF} variant='secondary' withArrow>
                                Start a Project
                            </ButtonLink>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    )
}
